/**
 * API functions for license key registration and validation
 */

import type {
  RegisterLicenseRequest,
  RegisterLicenseResponse,
  ValidateKeyRequest,
  ValidateKeyResponse,
} from './types';

const DEFAULT_REGISTER_BASE = 'https://lti13.alchem.ie';

function getRegisterApiBase(): string {
  return (import.meta.env?.VITE_REGISTER_API_BASE ?? DEFAULT_REGISTER_BASE).replace(/\/$/, '');
}

export function getLicenseApiBase(): string {
  // Prefer the consolidated VITE_API_BASE (alchemie-commerce Functions origin).
  // Falls back to the legacy VITE_LICENSE_API_BASE if set, then to lti13.
  const raw =
    import.meta.env?.VITE_API_BASE ??
    import.meta.env?.VITE_LICENSE_API_BASE ??
    DEFAULT_REGISTER_BASE;
  return raw.replace(/\/$/, '');
}

/**
 * Register a user and obtain a license key for a product
 *
 * @param request - Registration data including productId, name, email, and optional institution
 * @returns Promise resolving to license key and isNew flag
 * @throws Error if registration fails or validation errors occur
 */
export async function registerLicense(
  request: RegisterLicenseRequest
): Promise<RegisterLicenseResponse> {
  console.log('[Auth API] Registering license for product:', request.productId);
  console.log('[Auth API] User email:', request.email);

  try {
    const response = await fetch(`${getRegisterApiBase()}/registerLicense`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    const raw = await response.text();
    let data: RegisterLicenseResponse;
    try {
      data = raw ? (JSON.parse(raw) as RegisterLicenseResponse) : ({} as RegisterLicenseResponse);
    } catch {
      if (!response.ok) {
        throw new Error(
          `Registration failed: ${response.status} ${response.statusText} (server returned non-JSON body)`
        );
      }
      throw new Error('Invalid response from license server');
    }

    if (!response.ok) {
      console.error('[Auth API] Registration failed:', data.error || response.statusText);
      throw new Error(data.error || `Registration failed: ${response.status}`);
    }

    if (!data.success) {
      console.error('[Auth API] Registration unsuccessful:', data.error);
      throw new Error(data.error || 'Registration failed');
    }

    console.log('[Auth API] Registration successful, isNew:', data.isNew);
    return data;
  } catch (error) {
    console.error('[Auth API] Error during registration:', error);
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to register license');
  }
}

/**
 * Validate a license key against the alchemie-commerce /validateLicense endpoint.
 *
 * Treats `status: 'past_due'` as valid so the client shows a grace-period
 * banner rather than locking the user out (industry-standard Stripe dunning).
 *
 * @param key - The license key (JWT) to validate
 * @param productId - Optional productId; server verifies the JWT matches when provided
 * @returns Promise resolving to true if the license is usable (active or past_due), false otherwise
 */
export async function validateKey(key: string, productId?: string): Promise<boolean> {
  const result = await validateKeyDetailed(key, productId);
  return result.valid;
}

/**
 * Like `validateKey` but returns the full server response so callers can
 * surface richer UX (status banner, expiry date, reason code).
 */
export async function validateKeyDetailed(
  key: string,
  productId?: string
): Promise<ValidateKeyResponse> {
  console.log('[Auth API] Validating license key');

  try {
    const request: ValidateKeyRequest = productId ? { key, productId } : { key };

    const response = await fetch(`${getLicenseApiBase()}/validateLicense`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(request),
    });

    if (!response.ok) {
      console.error('[Auth API] Validation request failed:', response.status);
      return { valid: false, reason: `http_${response.status}` };
    }

    const data = (await response.json()) as ValidateKeyResponse;
    const isGracefulPastDue = data.status === 'past_due';
    const valid = data.valid === true || isGracefulPastDue;

    console.log('[Auth API] Validation result:', {
      valid,
      status: data.status,
      reason: data.reason,
    });

    return { ...data, valid };
  } catch (error) {
    console.error('[Auth API] Error during validation:', error);
    return { valid: false, reason: 'network_error' };
  }
}
